import Dropzone, { DropzoneProps } from 'react-dropzone';
import { useController, useFormContext } from 'react-hook-form';

import { useApi } from '@hooks/useApi';
import { cx } from 'class-variance-authority';
import { Key, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid4 } from 'uuid';
import { FormInputProps } from '../props';
import { MAX_UPLOAD_BYTES } from './constants';
import { DropPlaceholder } from './DropPlaceholder';
import { FilePreview } from './FilePreview';

export interface DropzoneFile {
    id?: string;
    name?: string;
    mime_type?: string;

    uid: string;
    file: File & {
        path?: string;
    };
}

export interface FileModel {
    id: number;
    name: string;
    tags: string[];
    type: string;
    url: string;
    lastModified: string;
}

interface Props extends FormInputProps {
    dropzone?: DropzoneProps;
    disableCropper?: boolean;
    cropperOptions?: Cropper.GetCroppedCanvasOptions;
    aspectRatio?: number;
    image?: boolean;
    valueMapping?: (id: Key) => any;
    className?: string;
    /** Shows up/down buttons; the field value follows the order on screen. */
    sortable?: boolean;
    /** True while a dropped file has no stored id yet, i.e. it is still uploading or failed. */
    onPendingChange?: (pending: boolean) => void;
}

export const FormDropzone = ({
    name,
    dropzone,
    cropperOptions,
    aspectRatio,
    image,
    valueMapping,
    className,
    sortable,
    onPendingChange
}: Props) => {
    const api = useApi();
    const { t } = useTranslation('form');
    const maxSize = dropzone?.maxSize ?? MAX_UPLOAD_BYTES;
    const maxSizeLabel = `${Math.round(maxSize / 1024 / 1024)} MB`;
    const [isLoading, setIsLoading] = useState(true);
    const { setError, clearErrors } = useFormContext();
    const [files, setFiles] = useState<DropzoneFile[]>([]);
    const { control } = useFormContext();
    const {
        field: { onChange, value },
        formState: { errors }
    } = useController({ name, control });

    const deleteFile = useCallback(
        (file: any) => {
            setFiles((prevFiles) => {
                const nextFiles = prevFiles.filter((f) => f.uid !== file.uid);
                onChange(nextFiles.map((entry) => (valueMapping ? valueMapping(entry.id) : entry.id)));
                return nextFiles;
            });
        },
        [onChange, valueMapping]
    );

    const updateFile = useCallback(
        (file: DropzoneFile) => {
            setFiles((prevFiles) => {
                const updatedFiles = prevFiles.map((f) => (f.uid === file.uid ? { ...f, ...file } : f));
                onChange(updatedFiles.map((entry) => (valueMapping ? valueMapping(entry.id) : entry.id)));
                return updatedFiles;
            });
        },
        [onChange, valueMapping]
    );

    const moveFile = useCallback(
        (file: DropzoneFile, direction: -1 | 1) => {
            setFiles((prevFiles) => {
                const from = prevFiles.findIndex((f) => f.uid === file.uid);
                const to = from + direction;
                if (from < 0 || to < 0 || to >= prevFiles.length) return prevFiles;

                const nextFiles = [...prevFiles];
                [nextFiles[from], nextFiles[to]] = [nextFiles[to], nextFiles[from]];
                onChange(nextFiles.map((entry) => (valueMapping ? valueMapping(entry.id) : entry.id)));
                return nextFiles;
            });
        },
        [onChange, valueMapping]
    );

    const hasPending = files.some((file) => !file.id);

    useEffect(() => {
        onPendingChange?.(hasPending);
    }, [hasPending]);

    // initially set files from value if available
    useEffect(() => {
        if (value && Array.isArray(value) && value.length > 0) {
            api({
                url: image ? 'image/meta' : 'file/meta',
                data: {
                    ids: value.map((file) => (typeof file === 'object' && 'id' in file ? file.id : file))
                },
                onFinish: () => setIsLoading(false),
                onSuccess: ({ data }) =>
                    setFiles(data.map((entry: DropzoneFile) => ({ ...entry, uid: entry.uid ?? uuid4() })))
            });
        } else {
            setIsLoading(false);
        }
    }, [image]);

    return (
        <div className={cx('tw-mb-4 tw-space-y-2', className)}>
            <Dropzone
                {...dropzone}
                disabled={isLoading}
                maxSize={maxSize}
                accept={image ? ['image/*'] : dropzone?.accept}
                onDrop={(acceptedFiles) => {
                    setFiles((prevFiles) => [
                        ...prevFiles,
                        ...acceptedFiles.map((file) => ({ file, uid: uuid4() }))
                    ]);
                    clearErrors(name);
                }}
                onDropRejected={(rejections) => {
                    const tooLarge = rejections.some((rejection) =>
                        rejection.errors.some((error) => error.code === 'file-too-large')
                    );

                    setError(name, {
                        type: 'manual',
                        message: tooLarge ? t('dropzone.tooLarge', { size: maxSizeLabel }) : t('dropzone.rejected')
                    });
                }}
            >
                {({ getRootProps, getInputProps, ...dropzoneProps }) => {
                    return (
                        <div {...getRootProps()}>
                            <input {...getInputProps()} />
                            <DropPlaceholder isLoading={isLoading} name={name} {...dropzoneProps} />
                        </div>
                    );
                }}
            </Dropzone>

            {files?.map((file, index) => (
                <FilePreview
                    error={errors?.[name]?.[index]}
                    key={file.uid}
                    file={file}
                    updateFile={updateFile}
                    deleteFile={deleteFile}
                    cropperOptions={cropperOptions}
                    aspectRatio={aspectRatio}
                    image={image}
                    onMoveUp={sortable && index > 0 ? () => moveFile(file, -1) : undefined}
                    onMoveDown={sortable && index < files.length - 1 ? () => moveFile(file, 1) : undefined}
                />
            ))}
        </div>
    );
};
